import { ShoppingCart } from "lucide-react";
import { useContext, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "shared/contexts/Auth/AuthContext";
import { useFavoritos } from "shared/contexts/Auth/Favoritos/FavoritosContext";
import { CartContext } from "shared/contexts/Cart/CartContext";
import { IProduto } from "shared/interfaces/IProduto";
import { buscarAvaliacoesProduto, IAvaliacaoProduto } from "shared/services/api/compras.api";
import { AvaliacaoEstrelas } from "../avaliacaoestrelas/AvaliacaoEstrelas";
import { PrecoFaixa } from "../preco/PrecoFaixa";
import styles from "./ProdutoCard.module.scss";

interface ProdutoCardProps {
  produto: IProduto;
  mostrarCarrinho?: boolean;
  variant?: "default" | "indicados";
}

export const ProdutoCard: React.FC<ProdutoCardProps> = ({ produto, mostrarCarrinho = true, variant = "default" }) => {
  const { signed } = useAuth();
  const { favoritesIds, addFavorite, removeFavorite } = useFavoritos();
  const { addItemCart } = useContext(CartContext);
  const navigate = useNavigate();
  const [avaliacoes, setAvaliacoes] = useState<IAvaliacaoProduto[]>([]);
  const [loadingAvaliacoes, setLoadingAvaliacoes] = useState(true);
  const isFavorito = favoritesIds.includes(produto.id);

  useEffect(() => {
    let ativo = true;

    async function carregarAvaliacoes() {
      if (!produto.id) {
        if (ativo) {
          setAvaliacoes([]);
          setLoadingAvaliacoes(false);
        }
        return;
      }

      try {
        setLoadingAvaliacoes(true);
        const resultado = await buscarAvaliacoesProduto(produto.id, 1, 50);
        if (ativo) setAvaliacoes(resultado);
      } catch (error) {
        if (ativo) setAvaliacoes([]);
      } finally {
        if (ativo) setLoadingAvaliacoes(false);
      }
    }

    carregarAvaliacoes();

    return () => {
      ativo = false;
    };
  }, [produto.id]);

  const mediaAvaliacao = avaliacoes.length > 0
    ? avaliacoes.reduce((total, avaliacao) => total + Number(avaliacao.rating || 0), 0) / avaliacoes.length
    : 0;

  const quantidadeAvaliacoes = avaliacoes.length;

  const toggleFavorite = async () => {
    try {
      if (isFavorito) {
        await removeFavorite(produto.id);
        toast.success("Removido dos favoritos");
      } else {
        await addFavorite(produto.id);
        toast.success("Adicionado aos favoritos");
      }
    } catch (error) {
      toast.error("Erro ao atualizar favorito");
    }
  };

  const handleAddCart = (e: React.MouseEvent<SVGSVGElement>) => {
    e.preventDefault();
    e.stopPropagation();

    if (!signed) {
      toast.error("Faça login para adicionar produtos ao carrinho.");
      return;
    }

    // Com variações, o cliente escolhe a combinação na página do produto.
    if (produto.hasVariations) {
      navigate(`/produto/${produto.id}`);
      return;
    }

    addItemCart(produto, 1);
    toast.success("Produto adicionado ao carrinho");
  };

  return (
    <div className={`${styles.containerProduto} ${variant === "indicados" ? styles.indicados : ""}`}>
      <div className={styles.containerImg}>
        <Link to={`/produto/${produto.id}`}>
          <img
            src={produto.gallery?.[0] ?? "/imagens/semfoto.jpeg"}
            className={styles.imgProduto}
            alt={produto.name ?? "Produto"}
            onError={(e) => {
              e.currentTarget.src = "/imagens/semfoto.jpeg";
            }}
          />
        </Link>

        {signed && (
          <div className={styles.containerFavorito}>
            <img
              onClick={toggleFavorite}
              className={styles.favoritoIcon}
              src={isFavorito ? "/icons/heartFilled.png" : "/icons/heart.png"}
              alt="favorito"
            />
          </div>
        )}
      </div>

      <div className={styles.containerInfo}>
        <p className={styles.produtoNome}>{produto.name ?? "Produto sem nome"}</p>

        <div className={styles.containerAvaliacoes}>
          {loadingAvaliacoes ? (
            <>
              <AvaliacaoEstrelas rating={0} />
              <p className={styles.avaliados}>(0)</p>
            </>
          ) : (
            <>
              <AvaliacaoEstrelas rating={mediaAvaliacao} />
              <p className={styles.avaliados}>({quantidadeAvaliacoes})</p>
            </>
          )}
        </div>

        <div className={styles.containerVenda}>
          <p className={styles.preco}>
            <PrecoFaixa produto={produto} classeSimbolo={styles.moeda} />
          </p>

          {mostrarCarrinho && (
            <ShoppingCart className={styles.iconCart} onClick={handleAddCart} />
          )}
        </div>
      </div>
    </div>
  );
};
